import React, { useCallback, useMemo, useState } from 'react';

import { Button, Checkbox, FontAwesomeIcon } from '@appcharge/shared-ui';
import { CircularProgress, Drawer } from '@mui/material';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'components/ui/tooltip';

import { SchemaForm } from './components/DynamicForm/SchemaForm';
import { RequestPanel } from './components/RequestPanel';
import { ResponsePanel } from './components/ResponsePanel';
import { INTEGRATION_TEST_MODAL_CONSTANTS } from './integration-test-modal.constants';
import { IIntegrationTestModalProps } from './integration-test-modal.types';
import { useIntegrationTest } from './use-integration-test.hook';

export const IntegrationTestModal: React.FC<IIntegrationTestModalProps> = ({
  isOpen,
  onClose,
  title,
  schema,
  url,
  fetchHeaders,
  defaultValues,
  schemaNames,
  selectedSchemaName,
  onSchemaChange,
  onTest,
  isLoading: isDataLoading,
  schemaSelectorLabel = 'Schema'
}) => {
  const [hideOptionalFields, setHideOptionalFields] = useState(true);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const {
    formValues,
    updateFormValue,
    resetForm,
    response,
    isLoading: isTestLoading,
    executeTest,
    requestBody,
    isFormValid,
    missingRequiredFields,
    headers,
    isHeadersReady
  } = useIntegrationTest({
    schema,
    fetchHeaders,
    isOpen,
    defaultValues,
    onTest
  });

  const handleClose = useCallback((): void => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleHideOptionalFieldsChange = useCallback((checked: boolean): void => {
    setHideOptionalFields(checked);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e?.preventDefault();
      await executeTest();
    },
    [executeTest]
  );

  const formTitle = useMemo(
    () => schema.title || INTEGRATION_TEST_MODAL_CONSTANTS.DEFAULT_TITLE,
    [schema.title]
  );

  const formDescription = useMemo(
    () => schema.description || INTEGRATION_TEST_MODAL_CONSTANTS.DEFAULT_DESCRIPTION,
    [schema.description]
  );

  const schemaSelectOptions = useMemo(
    () =>
      schemaNames.map((name) => ({
        label: name,
        value: name
      })),
    [schemaNames]
  );

  const defaultSelectedSchema = useMemo(() => {
    if (!selectedSchemaName) {
      return undefined;
    }
    return schemaSelectOptions.find((opt) => opt.value === selectedSchemaName);
  }, [schemaSelectOptions, selectedSchemaName]);

  const showSchemaSelector = useMemo(
    () => schemaNames.length > 1,
    [schemaNames]
  );

  const isButtonDisabledDueToValidation = useMemo(
    () => !isFormValid && !isTestLoading && !isDataLoading,
    [isFormValid, isTestLoading, isDataLoading]
  );

  const isTestButtonDisabled = useMemo(
    () => isTestLoading || isDataLoading || !isFormValid || !isHeadersReady,
    [isDataLoading, isFormValid, isHeadersReady, isTestLoading]
  );

  const handleButtonWrapperClick = useCallback((e: React.MouseEvent<HTMLSpanElement>): void => {
    if (isButtonDisabledDueToValidation) {
      e.preventDefault();
      e.stopPropagation();
      setIsTooltipOpen(true);
    }
  }, [isButtonDisabledDueToValidation]);

  const handleTooltipOpenChange = useCallback((open: boolean): void => {
    if (isButtonDisabledDueToValidation) {
      setIsTooltipOpen(open);
    }
  }, [isButtonDisabledDueToValidation]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <div className="flex h-full w-full flex-col">
        <div className="flex items-center justify-between border-b border-base-border px-6 py-4">
          <h2 className="text-hd-h4 text-base-foreground">{title}</h2>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip 
                open={isButtonDisabledDueToValidation ? isTooltipOpen : false}
                onOpenChange={handleTooltipOpenChange}
              >
                <TooltipTrigger asChild>
                  <span className="inline-block" onClick={handleButtonWrapperClick}>
                    <Button
                      type="submit"
                      form={INTEGRATION_TEST_MODAL_CONSTANTS.FORM_ID}
                      disabled={isTestButtonDisabled}
                      className="gap-2 bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      {isTestLoading ? INTEGRATION_TEST_MODAL_CONSTANTS.TESTING_BUTTON_TEXT : INTEGRATION_TEST_MODAL_CONSTANTS.TEST_BUTTON_TEXT}
                      <FontAwesomeIcon
                        icon="fa-solid fa-paper-plane"
                        className="h-4 w-4"
                      />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  className="rounded-md bg-base-card p-2 shadow-md border border-base-border text-sm"
                  side="bottom"
                >
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-base-cardForeground">Required fields:</p>
                    <p className="text-xs font-medium whitespace-normal break-words text-base-cardForeground">{missingRequiredFields.join(', ')}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <FontAwesomeIcon
                icon="fa-regular fa-xmark"
                className="h-3.5 w-3.5"
              />
            </Button>
          </div>
        </div>
        <div className="flex overflow-auto">
          <div className="w-[500px] px-[35px] py-[36px] flex-shrink-0 overflow-y-auto">
            {isDataLoading ? (
              <div className="flex h-64 items-center justify-center">
                <CircularProgress size={32} />
              </div>
            ) : (
              <>
                <div className="mb-6 space-y-1">
                  <h3 className="text-p-l font-semibold text-base-foreground">
                    {formTitle}
                  </h3>
                  <p className="text-p-s text-base-mutedForeground">
                    {formDescription}
                  </p>
                </div>
                <form id={INTEGRATION_TEST_MODAL_CONSTANTS.FORM_ID} onSubmit={handleSubmit}>

                  <div className="space-y-5">
                    <Checkbox
                      label="Hide optional fields"
                      checked={hideOptionalFields}
                      onCheckedChange={handleHideOptionalFieldsChange}
                    />
                    {showSchemaSelector && (
                      <InputSelectDropdown
                        shouldHighlightSelectedOption
                        options={schemaSelectOptions}
                        defaultSelected={defaultSelectedSchema}
                        onOptionChange={onSchemaChange}
                        label={schemaSelectorLabel}
                        placeholder="Select schema"
                      />
                    )}
                    <SchemaForm
                      schema={schema}
                      values={formValues}
                      onValueChange={updateFormValue}
                      hideOptionalFields={hideOptionalFields}
                    />
                  </div>
                </form>
              </>
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-8  mx-[35px] mt-[36px]">
            <RequestPanel url={url} headers={headers} body={requestBody} />
            {response?.statusCode !== undefined && (
              <ResponsePanel response={response} />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};
